import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { UserSearch } from '../../components/ui/UserSearch';
import { Avatar } from '../../components/Profile/Avatar';
import { X, UserPlus, Crown, Trash2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import { BottomSheet } from '../ui/BottomSheet';

interface GroupChatMembersProps {
  conversationId: string;
  onClose: () => void;
  isAdmin: boolean;
}

interface Member {
  id: string;
  username: string;
  avatar_url: string | null;
}

export function GroupChatMembers({ conversationId, onClose, isAdmin }: GroupChatMembersProps) {
  const [members, setMembers] = useState<Member[]>([]);
  const [adminId, setAdminId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddMember, setShowAddMember] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchMembers();
  }, [conversationId]);

  const fetchMembers = async () => {
    try {
      const { data: conversation, error: convError } = await supabase
        .from('conversations')
        .select('participant_ids, admin_id')
        .eq('id', conversationId)
        .single();

      if (convError) throw convError;

      setAdminId(conversation.admin_id);

      const { data: users, error: usersError } = await supabase
        .from('users')
        .select('id, username, avatar_url')
        .in('id', conversation.participant_ids);

      if (usersError) throw usersError;

      setMembers(users);
    } catch (err: any) {
      console.error('Error fetching members:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async (user: any) => {
    try {
      if (!isAdmin) {
        setError('Only the group admin can add members');
        return;
      }

      const { data: result, error: updateError } = await supabase
        .rpc('add_group_chat_member', {
          conversation_id: conversationId,
          new_member_id: user.id
        });

      if (updateError) {
        console.error('Error adding member:', updateError);
        throw new Error(`Failed to add member: ${updateError.message}`);
      }

      if (!result.success) {
        throw new Error(`Failed to add member: ${result.error}`);
      }

      setMembers([...members, user]);
      setShowAddMember(false);
    } catch (err: any) {
      console.error('Error adding member:', err);
      setError(err.message);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    try {
      if (!isAdmin) {
        setError('Only the group admin can remove members');
        return;
      }

      const { data: result, error: updateError } = await supabase
        .rpc('remove_group_chat_member', {
          conversation_id: conversationId,
          member_id: memberId
        });

      if (updateError) {
        console.error('Error removing member:', updateError);
        throw new Error(`Failed to remove member: ${updateError.message}`);
      }

      if (!result.success) {
        throw new Error(`Failed to remove member: ${result.error}`);
      }

      setMembers(members.filter(member => member.id !== memberId));
    } catch (err: any) {
      console.error('Error removing member:', err);
      setError(err.message);
    }
  };

  const handleDeleteGroup = async () => {
    if (!confirm('Are you sure you want to delete this group? This action cannot be undone.')) return;

    setDeleting(true);
    try {
      // Delete all messages in the conversation
      const { error: messagesError } = await supabase
        .from('messages')
        .delete()
        .eq('conversation_id', conversationId);

      if (messagesError) throw messagesError;

      // Delete the conversation
      const { error: conversationError } = await supabase
        .from('conversations')
        .delete()
        .eq('id', conversationId);

      if (conversationError) throw conversationError;

      onClose();
      window.location.href = '/chat';
    } catch (err: any) {
      console.error('Error deleting group:', err);
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg w-full max-w-md">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-text mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Mobile: BottomSheet */}
      <div className="lg:hidden">
        <BottomSheet
          isOpen={true}
          onClose={onClose}
          title="Group Members"
        >
          <div className="px-4 pb-6">
            {/* Admin badge */}
            <div className="flex items-center gap-2 bg-accent-base/10 px-3 py-2 rounded-lg mb-4">
              <Crown size={16} className="text-yellow-500" />
              <span className="text-sm text-content/70">Admin: {members.find(m => m.id === adminId)?.username || 'Unknown'}</span>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-lg mb-3 text-sm">
                {error}
              </div>
            )}

            {/* Members list */}
            <div className="space-y-2 mb-4">
              {members.map(member => (
                <div key={member.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Avatar url={member.avatar_url} userId={member.id} size="sm" editable={false} />
                    <div>
                      <span className="font-medium text-sm">{member.username}</span>
                      {member.id === adminId && (
                        <Badge variant="warning" className="ml-2 text-xs">Admin</Badge>
                      )}
                    </div>
                  </div>
                  {isAdmin && member.id !== adminId && (
                    <button
                      onClick={() => handleRemoveMember(member.id)}
                      className="text-red-500 p-2 hover:bg-red-50 rounded-full"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Admin controls */}
            {isAdmin && (
              <div className="space-y-3 pt-4 border-t border-gray-200">
                {showAddMember ? (
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium">Add Member</span>
                      <button onClick={() => setShowAddMember(false)} className="text-gray-500 text-sm">Cancel</button>
                    </div>
                    <UserSearch
                      onSelect={handleAddMember}
                      placeholder="Search users..."
                      excludeUserIds={members.map(m => m.id)}
                    />
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => setShowAddMember(true)}
                      className="w-full flex items-center justify-center gap-2 py-2.5 bg-accent-text text-white rounded-xl text-sm font-medium"
                    >
                      <UserPlus size={18} />
                      Add Member
                    </button>
                    <button
                      onClick={handleDeleteGroup}
                      disabled={deleting}
                      className="w-full flex items-center justify-center gap-2 py-2.5 bg-red-500 text-white rounded-xl text-sm font-medium"
                    >
                      {deleting ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      ) : (
                        <>
                          <Trash2 size={18} />
                          Delete Group
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </BottomSheet>
      </div>

      {/* Desktop: Modal */}
      <div className="hidden lg:flex fixed inset-0 bg-black/50 items-center justify-center z-50">
        <div className="bg-white rounded-2xl w-full max-w-md mx-4 max-h-[80vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold">Group Members</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-5">
            {/* Admin badge */}
            <div className="flex items-center gap-2 bg-accent-base/10 px-3 py-2 rounded-lg mb-4">
              <Crown size={16} className="text-yellow-500" />
              <span className="text-sm text-content/70">Admin: {members.find(m => m.id === adminId)?.username || 'Unknown'}</span>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-lg mb-3 text-sm">
                {error}
              </div>
            )}

            {/* Members list */}
            <div className="space-y-2 mb-4">
              {members.map(member => (
                <div key={member.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Avatar url={member.avatar_url} userId={member.id} size="sm" editable={false} />
                    <div>
                      <span className="font-medium text-sm">{member.username}</span>
                      {member.id === adminId && (
                        <Badge variant="warning" className="ml-2 text-xs">Admin</Badge>
                      )}
                    </div>
                  </div>
                  {isAdmin && member.id !== adminId && (
                    <button
                      onClick={() => handleRemoveMember(member.id)}
                      className="text-red-500 p-2 hover:bg-red-50 rounded-full"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Footer controls */}
          {isAdmin && (
            <div className="px-5 py-4 border-t border-gray-100 space-y-3">
              {showAddMember ? (
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium">Add Member</span>
                    <button onClick={() => setShowAddMember(false)} className="text-gray-500 text-sm">Cancel</button>
                  </div>
                  <UserSearch
                    onSelect={handleAddMember}
                    placeholder="Search users..."
                    excludeUserIds={members.map(m => m.id)}
                  />
                </div>
              ) : (
                <>
                  <button
                    onClick={() => setShowAddMember(true)}
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-accent-text text-white rounded-xl text-sm font-medium"
                  >
                    <UserPlus size={18} />
                    Add Member
                  </button>
                  <button
                    onClick={handleDeleteGroup}
                    disabled={deleting}
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-red-500 text-white rounded-xl text-sm font-medium"
                  >
                    {deleting ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    ) : (
                      <>
                        <Trash2 size={18} />
                        Delete Group
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
